import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Input, message, Modal, Popconfirm, Select, Space, Switch, Table, Tooltip, Typography } from "antd";
import { CloudUploadOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { api, AUTH_ROLE_KEY } from "../services/apiClient";
import { canUseUserFilter, fetchUserOptions, userQuery } from "../utils/adminUserFilter";

const { Title, Text } = Typography;

const byId = (items) => Object.fromEntries((items || []).map((item) => [item.id, item]));
const countValue = (value) => Number(value || 0).toLocaleString("vi-VN");

function labelOf(item, fields) {
  return fields.map((field) => item?.[field]).find(Boolean) || item?.id || "Không rõ";
}

export default function AutoConfigs() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [channels, setChannels] = useState([]);
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [facebookPages, setFacebookPages] = useState([]);
  const [uploadGroups, setUploadGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState();
  const [sourceErrorConfig, setSourceErrorConfig] = useState(null);
  const history = useHistory();
  const isSuperAdmin = localStorage.getItem(AUTH_ROLE_KEY) === "SUPER_ADMIN";
  const showUserFilter = canUseUserFilter();

  const channelById = useMemo(() => byId(channels), [channels]);
  const tiktokById = useMemo(() => byId(tiktokAccounts), [tiktokAccounts]);
  const facebookById = useMemo(() => byId(facebookPages), [facebookPages]);
  const groupById = useMemo(() => byId(uploadGroups), [uploadGroups]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [configsResult, channelsResult, tiktokResult, facebookResult, groupsResult] = await Promise.all([
        api.post("/api/configs/search", { keySearch: search, page: 1, size: 50, userId: selectedUserId || null }),
        api.get(`/api/channels${userQuery(selectedUserId)}`),
        api.get(`/api/tiktok-accounts${userQuery(selectedUserId)}`),
        api.get(`/api/facebook-pages${userQuery(selectedUserId)}`),
        api.get(`/api/upload-groups${userQuery(selectedUserId)}`),
      ]);
      setData(configsResult?.data || []);
      setChannels(channelsResult || []);
      setTiktokAccounts(tiktokResult || []);
      setFacebookPages(facebookResult || []);
      setUploadGroups(groupsResult || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [search, selectedUserId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!showUserFilter) return;
    fetchUserOptions().then(setUsers).catch((error) => message.error(error.message));
  }, [showUserFilter]);

  const toggleActive = async (item, active) => {
    try {
      const result = await api.post(`/api/configs/${item.id}/active`, { active });
      const updated = result?.data || result;
      setData((values) => values.map((value) => (value.id === item.id ? updated : value)));
      message.success(active ? "Đã active cấu hình" : "Đã inactive cấu hình");
    } catch (error) {
      message.error(error.message);
    }
  };

  const toggleAutoFollow = async (item, autoFollow) => {
    try {
      const result = await api.post(`/api/configs/${item.id}/auto-follow`, { autoFollow });
      const updated = result?.data || result;
      setData((values) => values.map((value) => (value.id === item.id ? updated : value)));
      message.success(autoFollow ? "Đã bật tự theo dõi nguồn" : "Đã tắt tự theo dõi nguồn");
    } catch (error) {
      message.error(error.message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/configs/${id}`);
      message.success("Đã xóa cấu hình");
      load();
    } catch (error) {
      message.error(error.message);
    }
  };

  const openView = (item) => {
    history.push(`/auto-upload/configs/${item.id}?mode=view`);
  };

  const openEdit = (item) => {
    history.push(`/auto-upload/configs/${item.id}`);
  };

  const renderUploadInfo = (item) => {
    const group = groupById[item.uploadGroupId];
    const uploadTargets = [
      ...(item.youtubeChannelIds || []).map((id) => `YouTube: ${labelOf(channelById[id], ["channelTitle", "channelName", "channelId"])}`),
      ...(item.tiktokAccountIds || []).map((id) => `TikTok: ${labelOf(tiktokById[id], ["displayName", "username", "accountId"])}`),
      ...(item.facebookPageIds || []).map((id) => `Facebook: ${labelOf(facebookById[id], ["pageName", "pageId", "pageUrl"])}`),
    ];

    return (
      <div className="auto-upload-info">
        <div className="auto-upload-group">
          <Text strong>{group?.name || "Chưa chọn nhóm"}</Text>
        </div>
        <div className="auto-upload-target-list">
          {uploadTargets.map((name, index) => <Text key={index} className="auto-upload-target" type="secondary">{name}</Text>)}
          {!uploadTargets.length && <Text className="auto-upload-target" type="secondary">Chưa có đối tượng upload</Text>}
        </div>
      </div>
    );
  };


  const sourceErrors = (item) => (item?.sourceDetails || []).filter((source) => source.status === "ERROR" || source.errorMessage);

  const renderStatus = (item) => {
    const errors = sourceErrors(item);
    return (
      <Space size={6} align="center">
        <Switch
          checked={Boolean(item.active)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
          onChange={(checked) => toggleActive(item, checked)}
        />
        {isSuperAdmin && errors.length > 0 ? (
          <Tooltip title="Xem lỗi nguồn video">
            <Button
              aria-label="Xem lỗi nguồn video"
              className="provider-action-button source-error-button"
              danger
              icon={<ExclamationCircleOutlined />}
              onClick={() => setSourceErrorConfig(item)}
              type="text"
            />
          </Tooltip>
        ) : null}
      </Space>
    );
  };

  const columns = [
    {
      title: "CẤU HÌNH",
      width: "25%",
      render: (_, item) => (
        <Space align="center" size={12}>
          <span className="auto-config-icon"><CloudUploadOutlined /></span>
          <div className="auto-config-source-cell">
            <Text strong>{item.configurationName}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "THÔNG TIN UPLOAD",
      width: "20%",
      render: (_, item) => renderUploadInfo(item),
    },
    {
      title: "UPLOAD THÀNH CÔNG",
      align: "center",
      width: "9%",
      render: (_, item) => <Text strong className="auto-count-success">{countValue(item.uploadSuccessCount)}</Text>,
    },
    {
      title: "UPLOAD THẤT BẠI",
      align: "center",
      width: "9%",
      render: (_, item) => <Text strong type="danger">{countValue(item.uploadFailedCount)}</Text>,
    },
    {
      title: "CHỜ UPLOAD",
      align: "center",
      width: "9%",
      render: (_, item) => <Text strong className="auto-count-waiting">{countValue(item.uploadWaitingCount)}</Text>,
    },
    {
      title: "TRẠNG THÁI",
      align: "center",
      width: "9%",
      render: (_, item) => renderStatus(item),
    },
    {
      title: "TỰ THEO DÕI NGUỒN",
      align: "center",
      width: "10%",
      render: (_, item) => (
        <Switch
          checked={Boolean(item.autoFollow)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
          onChange={(checked) => toggleAutoFollow(item, checked)}
        />
      ),
    },
    {
      title: "HÀNH ĐỘNG",
      align: "center",
      width: "9%",
      render: (_, item) => (
        <Space className="provider-actions auto-config-actions">
          <Tooltip title="Xem chi tiết">
            <Button aria-label="Xem chi tiết" className="provider-action-button" icon={<EyeOutlined />} onClick={() => openView(item)} />
          </Tooltip>
          <Tooltip title="Sửa cấu hình">
            <Button aria-label="Sửa cấu hình" className="provider-action-button" icon={<EditOutlined />} onClick={() => openEdit(item)} />
          </Tooltip>
          <Popconfirm title="Xóa cấu hình?" onConfirm={() => remove(item.id)}>
            <Tooltip title="Xóa cấu hình">
              <Button aria-label="Xóa cấu hình" danger className="provider-action-button" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-heading">
        <div>
          <Title level={3}>Cấu hinh Reup</Title>
          <Text type="secondary">Quản lý nguồn video, render và lịch đăng.</Text>
        </div>
      </div>
      <Card className="criclebox">
        <div className="auto-toolbar">
          <Input className="auto-toolbar-search" allowClear prefix={<SearchOutlined />} placeholder="Tìm cấu hình" value={search} onChange={(event) => setSearch(event.target.value)} />
          {showUserFilter ? (
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Tìm theo người dùng"
              style={{ minWidth: 240 }}
              value={selectedUserId}
              onChange={setSelectedUserId}
              options={users}
            />
          ) : null}
          <Button className="auto-toolbar-primary" type="primary" icon={<PlusOutlined />} onClick={() => history.push("/auto-upload/configs/new")}>Tạo cấu hình</Button>
        </div>
        <Table className="auto-config-table" rowKey="id" loading={loading} columns={columns} dataSource={data} tableLayout="fixed" />
      </Card>
      <Modal
        title="Lỗi nguồn video"
        visible={Boolean(sourceErrorConfig)}
        onCancel={() => setSourceErrorConfig(null)}
        footer={<Button type="primary" onClick={() => setSourceErrorConfig(null)}>Đóng</Button>}
        width={1140}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          {sourceErrors(sourceErrorConfig).map((source) => (
            <div className="source-error-item" key={source.id || source.url}>
              <Text strong>{source.url}</Text>
              <Text className="source-error-message" type="danger">{source.errorMessage || "Nguồn metadata lỗi nhưng chưa có thông tin chi tiết."}</Text>
            </div>
          ))}
        </Space>
      </Modal>
    </>
  );
}
